import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getProject } from '../../../../Requests/ProjectRequests';
import { getProjectConversations, createConversation, deleteConversation } from '../../../../Requests/ConversationRequests';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';
import { ChevronLeftIcon, PlusIcon } from '@heroicons/react/20/solid';
import { ChatBubbleLeftRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import ConversationDetail from '../../../../Components/Conversations/ConversationDetail';
import UpgradedLoader from '../../../../Components/UpgradedLoader';
import ConfirmationModal from '../../../../Components/ConfirmationModal';

function Conversations() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { project_uuid } = useParams();
    const { isCollapsed } = useSidebar();
    
    const [project, setProject] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showNewConversationForm, setShowNewConversationForm] = useState(false);
    const [newConversationTitle, setNewConversationTitle] = useState('');
    const [newConversationDescription, setNewConversationDescription] = useState('');
    const [creating, setCreating] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Load project and conversations
    useEffect(() => {
        if (project_uuid) {
            setLoading(true);
            Promise.all([
                getProject(project_uuid),
                getProjectConversations(project_uuid)
            ]).then(([projectResponse, conversationsResponse]) => {
                setProject(projectResponse.data);
                const conversationsData = conversationsResponse.data || [];
                setConversations(conversationsData);
                
                // Check if we need to pre-select a conversation from navigation state
                const selectedConversationId = location.state?.selectedConversationId;
                if (selectedConversationId && conversationsData.length > 0) {
                    const preSelectedConversation = conversationsData.find(c => c.uuid === selectedConversationId);
                    if (preSelectedConversation) {
                        setSelectedConversation(preSelectedConversation);
                    }
                }
            }).catch((error) => {
                console.error('Error loading project or conversations:', error);
                navigate('/projects');
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [project_uuid, navigate, location.state?.selectedConversationId]);

    // Handle creating new conversation
    const handleCreateConversation = async (e) => {
        e.preventDefault();
        if (!newConversationTitle.trim()) return;

        setCreating(true);
        try {
            const conversationData = {
                title: newConversationTitle.trim(),
                description: newConversationDescription.trim()
            };

            const response = await createConversation(project_uuid, conversationData);
            const newConversation = response.data;

            setConversations([newConversation, ...conversations]);
            setSelectedConversation(newConversation);
            
            // Reset form
            setNewConversationTitle('');
            setNewConversationDescription('');
            setShowNewConversationForm(false);
        } catch (error) {
            console.error('Error creating conversation:', error);
            // TODO: Add error notification
        } finally {
            setCreating(false);
        }
    };

    // Handle conversation update (memoized to prevent excessive API calls)
    const handleConversationUpdate = useCallback((updatedConversation) => {
        setConversations(conversations.map(c => 
            c.uuid === updatedConversation.uuid ? updatedConversation : c
        ));
        setSelectedConversation(updatedConversation);
    }, [conversations]);

    // Handle deleting conversation
    const handleDeleteConversation = (conversation) => {
        setConversationToDelete(conversation);
        setDeleteModalOpen(true);
    };

    const confirmDeleteConversation = async () => {
        if (!conversationToDelete) return;

        try {
            setDeleting(true);
            await deleteConversation(conversationToDelete.uuid);
            const updatedConversations = conversations.filter(c => c.uuid !== conversationToDelete.uuid);
            setConversations(updatedConversations);
            
            // If deleted conversation was selected, clear selection
            if (selectedConversation?.uuid === conversationToDelete.uuid) {
                setSelectedConversation(null);
            }
            
            setDeleteModalOpen(false);
            setConversationToDelete(null);
        } catch (error) {
            console.error('Error deleting conversation:', error);
            // TODO: Add error notification
        } finally {
            setDeleting(false);
        }
    };

    if (loading || !project) {
        return (
            <div className={`${getSidebarPadding(isCollapsed)} p-6`}>
                <div className="flex items-center justify-center py-12">
                    <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-black" />
                </div>
            </div>
        );
    }

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 w-full h-screen flex flex-col justify-start`}>
            <div className='flex flex-col w-full justify-start items-center px-4 h-full'>
                {/* Navigation Breadcrumb */}
                <div className="mb-6 w-full max-w-7xl">
                    <nav className="flex items-center space-x-2 text-sm text-gray-500">
                        <button
                            onClick={() => navigate('/projects')}
                            className="flex items-center hover:text-gray-700"
                        >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            {t('ProjectOverview.AllProjects')}
                        </button>
                        <span>›</span>
                        <button
                            onClick={() => navigate(`/project/${project_uuid}`)}
                            className="hover:text-gray-700"
                        >
                            {project.name}
                        </button>
                        <span>›</span>
                        <span className="text-gray-900 font-medium">Conversations</span>
                    </nav>
                </div>

                {/* Header */}
                <div className="bg-white max-w-7xl rounded-2xl w-full shadow-xl mb-6">
                    <div className="px-4 py-5 sm:px-6">
                        <div className="md:flex md:items-center md:justify-between">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-2xl/7 font-bold text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                                    {t('Conversations.title')}
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 sm:truncate">
                                    {t('Conversations.subtitle')}
                                </p>
                            </div>
                            <div className="mt-4 flex shrink-0 md:ml-4 md:mt-0 space-x-2">
                                <button
                                    type="button"
                                    onClick={() => setShowNewConversationForm(true)}
                                    className="inline-flex items-center rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
                                >
                                    <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                    {t('Conversations.newConversation')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex w-full max-w-7xl flex-1 min-h-0">
                    {/* Conversations List */}
                    <div className="w-1/3 bg-white rounded-2xl shadow-xl mr-6 flex flex-col">
                        <div className="px-4 py-3 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">{t('Conversations.title')}</h3>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto">
                            {conversations.length === 0 ? (
                                <div className="p-6 text-center">
                                    <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('Conversations.noConversations')}</h3>
                                    <p className="mt-1 text-sm text-gray-500">{t('Conversations.noConversationsDescription')}</p>
                                    <div className="mt-6">
                                        <button
                                            type="button"
                                            onClick={() => setShowNewConversationForm(true)}
                                            className="inline-flex items-center rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200"
                                        >
                                            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                            New Conversation
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {conversations.map((conversation) => (
                                        <div
                                            key={conversation.uuid}
                                            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                                                selectedConversation?.uuid === conversation.uuid ? 'bg-iavisus-50 border-r-2 border-iavisus-100' : ''
                                            }`}
                                            onClick={() => setSelectedConversation(conversation)}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                                        {conversation.title}
                                                    </h4>
                                                    {conversation.description && (
                                                        <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                                                            {conversation.description}
                                                        </p>
                                                    )}
                                                    <div className="mt-2 flex items-center text-xs text-gray-400">
                                                        <span>
                                                            {conversation.notes_count || 0} notes
                                                        </span>
                                                        {conversation.unread_count > 0 && (
                                                            <>
                                                                <span className="mx-1">•</span>
                                                                <span className="text-iavisus-100 font-medium">
                                                                    {conversation.unread_count} unread
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteConversation(conversation);
                                                    }}
                                                    className="ml-2 text-gray-400 hover:text-red-500"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Conversation Detail */}
                    <div className="flex-1 bg-white rounded-2xl shadow-xl">
                        {selectedConversation ? (
                            <ConversationDetail
                                conversation={selectedConversation}
                                onConversationUpdate={handleConversationUpdate}
                            />
                        ) : (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center">
                                    <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('Conversations.selectConversation')}</h3>
                                    <p className="mt-1 text-sm text-gray-500">{t('Conversations.selectConversationDescription')}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* New Conversation Form Modal */}
            {showNewConversationForm && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowNewConversationForm(false)} />
                        
                        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <form onSubmit={handleCreateConversation}>
                                <div>
                                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                                        {t('Conversations.newConversation')}
                                    </h3>
                                    <div className="mt-4">
                                        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('Conversations.conversationTitle')}
                                        </label>
                                        <input
                                            type="text"
                                            name="title"
                                            id="title"
                                            required
                                            value={newConversationTitle}
                                            onChange={(e) => setNewConversationTitle(e.target.value)}
                                            className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-iavisus-100 sm:text-sm sm:leading-6"
                                            placeholder={t('Conversations.conversationTitlePlaceholder')}
                                        />
                                    </div>
                                    <div className="mt-4">
                                        <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('Conversations.conversationDescription')}
                                        </label>
                                        <textarea
                                            name="description"
                                            id="description"
                                            rows={3}
                                            value={newConversationDescription}
                                            onChange={(e) => setNewConversationDescription(e.target.value)}
                                            className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-iavisus-100 sm:text-sm sm:leading-6"
                                            placeholder={t('Conversations.conversationDescriptionPlaceholder')}
                                        />
                                    </div>
                                </div>
                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                                    <button
                                        type="submit"
                                        disabled={creating || !newConversationTitle.trim()}
                                        className="inline-flex w-full justify-center rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100 disabled:opacity-50 disabled:cursor-not-allowed sm:col-start-2"
                                    >
                                        {creating ? t('Conversations.creating') : t('Conversations.createConversation')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowNewConversationForm(false);
                                            setNewConversationTitle('');
                                            setNewConversationDescription('');
                                        }}
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                                    >
                                        {t('common.cancel')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Conversation Confirmation Modal */}
            <ConfirmationModal
                open={deleteModalOpen}
                setOpen={setDeleteModalOpen}
                title={t('Conversations.deleteConversationTitle')}
                message={t('Conversations.deleteConversationMessage', { title: conversationToDelete?.title })}
                warning={t('Conversations.deleteConversationWarning')}
                onConfirm={confirmDeleteConversation}
                loading={deleting}
                variant="danger"
                confirmText={t('Conversations.deleteConversation')}
                cancelText={t('common.cancel')}
            />
        </div>
    );
}

export default Conversations;